<template>
    <div class="campaign-box campaign-box--no-top-border campaign-box--top-padding" data-dusk="add-funds">
        <div class="col-md-12 text-center" v-if="loading">
            <i class="fa fa-spinner fa-spin inner-loading"></i>
        </div>

        <form @submit.prevent="showAuthorizationModal" data-dusk="ready" v-else>
            <div class="row">
                <div class="col-sm-12">
                    <validation-errors :errors="errors"></validation-errors>
                </div>

                <div class="col-sm-4">
                    <p class="m-b-lg text-muted">
                        Add money to your account by selecting your Product Category, Funding Source, entering Funding Amount and clicking Add Funds button.
                        <strong v-if="feePercentText">{{ feePercentText }}</strong>
                        Minimum funding amount is ${{ minimumFundingAmount | currency }}.
                    </p>

                    <div v-if="showAccountBalance">
                        <h3 style="margin-bottom:0px;">${{ Number(accountBalanceCents) / 100 | currency }}</h3>
                        <p class="text-muted">
                            {{ accountName }} Account Balance
                        </p>
                    </div>
                </div>

                <div class="col-sm-8">
                    <div class="form-group" v-validation-field:fundsPoolId="errors">
                        <label class="control-label">Product Category</label>

                        <select class="form-control" data-dusk="fundsPoolId" v-model="fundsPoolId">
                            <option :value="fundsPool.id" v-for="fundsPool in fundsPools">{{ fundsPool.name }}</option>
                        </select>
                    </div>

                    <div class="form-group" v-validation-field:fundingSourceId="errors">
                        <label class="control-label">Funding Source</label>

                        <select class="form-control" data-dusk="fundingSourceId" v-model="fundingSourceId">
                            <option disabled value="">Select a Card</option>
                            <option :value="fundingSource.id" v-for="fundingSource in fundingSources">{{ fundingSource.name }}</option>
                        </select>
                    </div>

                    <div class="form-group" v-validation-field:amount="errors">
                        <label class="control-label">Funding Amount</label>

                        <input class="form-control" type="text" data-dusk="amount" v-money="moneyConfig" v-model="amount">
                    </div>

                    <div class="form-group" v-if="fee">
                        <label class="col-sm-4 control-label" style="padding-left:0">Convenience Fee<br />
                        <span class="text-muted">${{ calculatedConvenienceFee | currency }}</span></label>

                        <label class="col-sm-4 control-label" style="padding-left:0">Total Amount<br />
                        <span class="text-muted">${{ calculatedTotalAmount | currency }}</span></label>
                    </div>
                </div>
            </div>

            <hr>

            <div class="pull-right">
                <button class="btn btn-success --edit" :class="{disabled: ! authorizationModalIsReady}" data-dusk="save">Add Funds</button>
            </div>
        </form>

        <billing-authorization
            @cancel="cancelAuthorization"
            @showErrors="showAuthorizationErrors"
            @success="refreshStep"
            :show="authorizationModalIsVisible"
            :fundingSource="selectedSource"
            :fundsPool="selectedFundsPool"
            :amount="amount"
            :fee="calculatedConvenienceFee"
            :totalAmount="calculatedTotalAmount"
            v-if="authorizationModalIsReady"
            >
        </billing-authorization>
    </div>
</template>

<script type="text/babel">
    import ValidationErrors from './../utils/validation-errors.vue';
    import ValidationField from './../../directives/validation-field';
    import { VMoney } from 'v-money';
    import Swal from "sweetalert2";

    export default {
        directives: { 'validation-field': ValidationField, VMoney },

        components: { 'validation-errors': ValidationErrors },

        props: {
            loading: Boolean,
            fundingSources: Array,
            fundsPools: Array,
            fundingMinimumsByProductGroup: Object,
        },

        data() {
            return {
                errors: {},
                authorizationModalIsVisible: false,
                moneyConfig: {
                    decimal: '.',
                    thousands: ',',
                    prefix: '$ ',
                    precision: 0,
                    // If mask is false, outputs the number to the model. Otherwise outputs the masked string.
                    masked: false /* doesn't work with directive */
                },
                accountName: '',
                accountBalanceCents: null,
                showAccountBalance: false,
                fundingSourceId: '',
                fundsPoolId: 0,
                fundsPool: {},
                amount: null,
                fee: null,
                feePercentText: ''
            }
        },

        created() {
            this.fundsPool = this.fundsPools[0];
            this.fundsPoolId = this.fundsPool.id;
            this.amount = this.minimumFundingAmount;
            this.getAccountBalance();
            this.setConvenienceFeeText();
        },

        watch: {
            fundsPoolId() {
                this.fundsPool = this.fundsPools.find(fundsPool => { return fundsPool.id === this.fundsPoolId })
                this.amount = this.minimumFundingAmount;
                this.getAccountBalance();
            }
        },

        computed: {
            authorizationModalIsReady() {
                return this.selectedSource
                    && this.selectedFundsPool
                    && this.amount;
            },

            selectedSource() {
                return _(this.fundingSources).find({id: this.fundingSourceId});
            },

            selectedFundsPool() {
                return _(this.fundsPools).find({id: this.fundsPoolId});
            },

            minimumFundingAmount() {
                return this.fundingMinimumsByProductGroup[this.fundsPool.productGroupId]
                    || this.fundingMinimumsByProductGroup['default'];
            },

            calculatedConvenienceFee() {
                const feePercent = this.selectedSource.convenienceFeePercent;
                return feePercent > 0 ? (this.fee = (feePercent * .01) * this.stripNonNumericCharacters(this.amount)) : 0;
            },

            calculatedTotalAmount() {
                return Number(this.stripNonNumericCharacters(this.amount) + this.fee);
            }
        },

        methods: {
            cancelAuthorization() {
                this.authorizationModalIsVisible = false;
            },

            showAuthorizationModal() {
                if (! this.authorizationModalIsReady) {
                    return;
                }

                this.authorizationModalIsVisible = true;
            },

            showAuthorizationErrors(errorResponse) {
                if (errorResponse.errors) {
                    this.errors = errorResponse.errors;
                } else if (errorResponse.message) {
                    this.errors = {message: [errorResponse.message]}
                } else {
                    new Swal('Whoops!', 'Something went wrong. Please contact support.', 'error');
                }

                this.authorizationModalIsVisible = false;
            },

            getAccountBalance() {
                this.showAccountBalance = false;
                this.$http.get(route('api.advertiser.account-balance.query', this.fundsPool.id))
                  .then(response => {
                    this.accountBalanceCents = response.data.balanceCents;
                    this.accountName =  this.fundsPool.name;
                    this.showAccountBalance = true;
                });
            },

            setConvenienceFeeText() {
                const convenienceFeePercent = this.fundingSources?.slice(-1)[0].convenienceFeePercent ?? 0;
                this.feePercentText = convenienceFeePercent === 0
                    ? ''
                    : `There is a ${convenienceFeePercent}% convenience fee that will be applied to your funding amount for all credit cards transactions.`;
            },

            refreshStep() {
                this.$emit('refreshStep');
            },

            stripNonNumericCharacters(amount) {
                return Number(amount.toString().replace(/[^0-9\.-]+/g,""));
            }
        }
    }
</script>
